<script setup lang="ts">
import {Button, Card, Column, DataTable, Paginator} from 'primevue';
import {PagedResult} from '@/app/shared/helpers/paged-result.ts';
import {DocumentType, type Invoice, PaymentMethod} from '@/api/enterprise-gateway-api.proxy.ts';
import {computed, type PropType} from 'vue';
import {useFormatting} from '@/composables/currency-format.ts';
import {useInvoicingStore} from '@/app/stores/invoicing-store.ts';
import AegPaginator from '@/app/shared/components/aeg-paginator/aeg-paginator.vue';
import {isEmpty} from 'lodash-es';
import AegDatatable from '@/app/shared/components/aeg-datatable/aeg-datatable.vue';

const props = defineProps({
    invoices: {type: Object as PropType<PagedResult<Invoice>>, default: () => PagedResult.createEmpty<Invoice>()},
    downloadFileUri: {type: String, default: null},
    showDownloadLink: {type: Boolean, default: false},
    isLoading: {type: Boolean, default: false},
    showPagination: {type: Boolean, default: true},
    showService: {type: Boolean, default: false},
    showCustomerReference: {type: Boolean, default: true},
    showDocumentType: {type: Boolean, default: true},
    showPaid: {type: Boolean, default: true},
    showActor: {type: Boolean, default: false},
});

const emit = defineEmits({
   changePage: (pageNr: number) => true,
});

const {formatCurrency, formatDate} = useFormatting();
const invoiceStore = useInvoicingStore();

const getInvoiceTypeIcon = (documentType?: DocumentType, paymentMethod?: PaymentMethod): string => {
    return invoiceStore.getInvoiceTypeIcon(documentType, paymentMethod);
};

const getDocumentTypeLabel = (documentType?: DocumentType): string => {
    return invoiceStore.getDocumentTypeLabel(documentType);
};

const isTopUpType = (documentType?: DocumentType): boolean => {
    return documentType === DocumentType.PaymentProof;
};

const hasInvoices = computed((): boolean => !isEmpty(props.invoices?.items));

</script>

<template>
    <aeg-datatable :value="invoices.items" sortField="documentDate" :sortOrder="-1" :loading="isLoading">
        <template #empty>
            U heeft nog geen facturen of betalingsbewijzen.
        </template>
        <template #footer>
            <div class="flex justify-between items-center">
                <Button
                    as="a"
                    v-if="showDownloadLink && hasInvoices"
                    icon="pi pi-download"
                    iconPos="left"
                    variant="link"
                    :href="downloadFileUri"
                    label="Download"
                />
                <aeg-paginator
                    v-if="showPagination && hasInvoices"
                    :paging-info="invoices.pagingInfo"
                    @change-page="(pageNr: number) => emit('changePage', pageNr)"
                />
            </div>
        </template>
        <Column header="Datum" :field="(invoice) => formatDate(invoice.documentDate)" :sortable="true" body-style="max-width: 8em;" />
        <Column field="invoiceNumber" header="Doc. nr." :sortable="true" body-style="max-width: 14em; white-space: nowrap;"></Column>
        <Column field="service" header="Service" :sortable="false" v-if="showService" body-class="aeg-uppercase"></Column>
        <Column header="Document" :field="(invoice) => getDocumentTypeLabel(invoice.documentType)" :sortable="true" v-if="showDocumentType" />
        <Column field="customerReference" header="Eigen referentie" v-if="showCustomerReference"></Column>
        <Column body-style="text-align: center; max-width: 2em;" v-if="showDocumentType">
            <template #body="slotProps">
                <aeg-icon :icon="getInvoiceTypeIcon(slotProps?.data?.documentType)" style="font-size: 1.125rem;" />
            </template>
        </Column>
        <Column field="totalPrice" header="Bedrag" header-style="direction: rtl; text-align: justify; max-width: 7em;" body-style="text-align: right;">
            <template #body="slotProps">
                <span v-if="isTopUpType(slotProps?.data?.documentType)" class="topup">
                    + {{formatCurrency(slotProps?.data?.totalPrice)}}
                </span>
                <span v-else>
                    {{formatCurrency(slotProps?.data?.totalPrice)}}
                </span>
            </template>
        </Column>
        <Column header="Betaald" body-style="text-align: center; max-width: 7em;" :pt="{ columnHeaderContent: { class: 'justify-center' }}" v-if="showPaid">
            <template #body="slotProps">
                <aeg-icon-check v-if="slotProps?.data?.paid" />
            </template>
        </Column>
        <Column header="PDF" body-style="text-align: center; padding:0; width: 3em;" :pt="{ columnHeaderContent: { class: 'justify-center' }}">
            <template #body="slotProps">
                <Button
                    v-if="slotProps?.data?.directPdfUrl"
                    as="a"
                    variant="link"
                    icon="pi pi-file-pdf"
                    aria-label="Download als pdf"
                    :href="slotProps?.data?.directPdfUrl"
                    size="large"
                    class="aeg-datatable-cell-button"
                    v-tooltip.left="'Download als pdf'"
                />
            </template>
        </Column>
        <Column header="CSV" body-style="text-align: center; center; padding:0; width: 3em;" :pt="{ columnHeaderContent: { class: 'justify-center' }}">
            <template #body="slotProps">
                <Button
                    v-if="slotProps?.data?.directCsvUrl"
                    as="a"
                    variant="link"
                    icon="pi pi-file-excel"
                    aria-label="Download als csv"
                    :href="slotProps?.data?.directCsvUrl"
                    size="large"
                    class="aeg-datatable-cell-button"
                    v-tooltip.left="'Download als csv'"
                />
            </template>
        </Column>
    </aeg-datatable>
</template>

<style scoped lang="scss">
.topup {
    color: green;
    white-space: nowrap;
}
</style>
